import { Document, Page, StyleSheet, Text, View } from "@react-pdf/renderer";

const styles = StyleSheet.create({
  page: {
    padding: 28,
    fontFamily: "Helvetica",
    fontSize: 11,
    color: "#1f2530",
  },
  header: {
    borderBottomWidth: 2,
    borderBottomColor: "#25374f",
    borderStyle: "solid",
    paddingBottom: 10,
    marginBottom: 16,
  },
  title: {
    fontSize: 18,
    color: "#25374f",
    fontWeight: "bold",
  },
  metaRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    marginTop: 6,
  },
  metaItem: {
    fontSize: 10,
    color: "#4b5563",
  },
  section: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#25374f",
    marginBottom: 8,
  },
  summaryGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 8,
  },
  summaryBlock: {
    flexBasis: "23%",
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderStyle: "solid",
    borderRadius: 6,
    padding: 10,
  },
  summaryLabel: {
    color: "#4b5563",
    marginBottom: 4,
  },
  summaryValue: {
    fontWeight: "bold",
    fontSize: 12,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#25374f",
    color: "white",
    paddingVertical: 8,
    paddingHorizontal: 10,
    fontWeight: "bold",
    fontSize: 10,
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
    borderStyle: "solid",
  },
  colFloor: { width: "18%" },
  colHeight: { width: "14%" },
  colConfig: { width: "18%" },
  colLength: { width: "14%" },
  colRisers: { width: "12%" },
  colPerFlight: { width: "12%" },
  colRiserHeight: { width: "12%" },
  muted: {
    color: "#6b7280",
  },
  placeholder: {
    height: 320,
    borderWidth: 1,
    borderColor: "#cbd5e1",
    borderStyle: "dashed",
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f8fafc",
  },
  placeholderText: {
    color: "#475569",
    fontSize: 12,
  },
  footer: {
    position: "absolute",
    bottom: 20,
    left: 28,
    right: 28,
    fontSize: 9,
    color: "#6b7280",
    flexDirection: "row",
    justifyContent: "space-between",
  },
});

const ORD_WORDS = [
  "First",
  "Second",
  "Third",
  "Fourth",
  "Fifth",
  "Sixth",
  "Seventh",
  "Eighth",
  "Ninth",
  "Tenth",
  "Eleventh",
  "Twelfth",
];

function storeyName(floorIndex, entranceIdx) {
  if (entranceIdx == null) return `Floor ${floorIndex + 1}`;
  const offset = floorIndex - entranceIdx;
  if (offset === 0) return "Ground Floor";
  if (offset < 0) {
    const n = Math.abs(offset);
    return n === 1 ? "Basement" : `Basement ${n}`;
  }
  if (offset <= ORD_WORDS.length) return `${ORD_WORDS[offset - 1]} Floor`;
  return `${offset}th Floor`;
}

const formatNumber = (value, digits = 2, suffix = "") => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return `${num.toFixed(digits)}${suffix}`;
};

const safeCount = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : "—";
};

const MetadataHeader = ({ project }) => {
  const projectName = project?.name || "Pebbledash project";
  const buildingUse = project?.building_use || "Office";
  const location = project?.local_authority || project?.postcode || "Location unknown";
  const dateLabel = new Date().toLocaleDateString();

  return (
    <View style={styles.header}>
      <Text style={styles.title}>Pebbledash Stair Sizing Report</Text>
      <View style={styles.metaRow}>
        <Text style={styles.metaItem}>Project: {projectName}</Text>
        <Text style={styles.metaItem}>Location: {location}</Text>
        <Text style={styles.metaItem}>Building use: {buildingUse}</Text>
        <Text style={styles.metaItem}>Date: {dateLabel}</Text>
      </View>
    </View>
  );
};

const SummaryBlock = ({ label, value }) => (
  <View style={styles.summaryBlock}>
    <Text style={styles.summaryLabel}>{label}</Text>
    <Text style={styles.summaryValue}>{value}</Text>
  </View>
);

const FloorTableHeader = () => (
  <View style={styles.tableHeader}>
    <Text style={styles.colFloor}>Floor</Text>
    <Text style={styles.colHeight}>Height (m)</Text>
    <Text style={styles.colConfig}>Configuration</Text>
    <Text style={styles.colLength}>Core length (m)</Text>
    <Text style={styles.colRisers}>Total risers</Text>
    <Text style={styles.colPerFlight}>Risers / flight</Text>
    <Text style={styles.colRiserHeight}>Riser height (mm)</Text>
  </View>
);

const FloorTableRow = ({ row, entranceIdx }) => (
  <View style={styles.tableRow}>
    <Text style={styles.colFloor}>{storeyName(row.floor_index, entranceIdx)}</Text>
    <Text style={styles.colHeight}>{formatNumber(row.floor_height_m, 2, " m")}</Text>
    <Text style={styles.colConfig}>{row.configuration || "—"}</Text>
    <Text style={styles.colLength}>{formatNumber(row.stair_core_length_m, 2, " m")}</Text>
    <Text style={styles.colRisers}>{safeCount(row.total_risers)}</Text>
    <Text style={styles.colPerFlight}>{safeCount(row.risers_per_flight)}</Text>
    <Text style={styles.colRiserHeight}>{formatNumber(row.actual_riser_height_mm, 0, " mm")}</Text>
  </View>
);

const KeyFloorSummary = ({ floor, entranceIdx }) => (
  <View style={styles.section}>
    <Text style={styles.sectionTitle}>Controlling floor summary</Text>
    {!floor ? (
      <Text style={styles.muted}>No floor data available.</Text>
    ) : (
      <View style={styles.summaryGrid}>
        <SummaryBlock label="Floor" value={storeyName(floor.floor_index, entranceIdx)} />
        <SummaryBlock label="Configuration" value={floor.configuration || "—"} />
        <SummaryBlock label="Core length" value={formatNumber(floor.stair_core_length_m, 2, " m")} />
        <SummaryBlock label="Risers per flight" value={safeCount(floor.risers_per_flight)} />
        <SummaryBlock label="Total risers" value={safeCount(floor.total_risers)} />
        <SummaryBlock label="Riser height" value={formatNumber(floor.actual_riser_height_mm, 0, " mm")} />
      </View>
    )}
  </View>
);

const PlaceholderDiagram = () => (
  <View style={styles.section}>
    <Text style={styles.sectionTitle}>Stair core diagram placeholder</Text>
    <View style={styles.placeholder}>
      <Text style={styles.placeholderText}>Diagram placeholder — replace with future drawing</Text>
    </View>
  </View>
);

const StairPDFDocument = ({ project, result, entranceIdx }) => {
  const floors = Array.isArray(result?.floors) ? result.floors : [];
  const controllingFloor = result?.controlling_floor_config || floors.find((f) => f.floor_index === result?.controlling_floor_index);

  return (
    <Document>
      <Page size="A3" orientation="landscape" style={styles.page}>
        <MetadataHeader project={project} />
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Stair core summary</Text>
          <View style={styles.summaryGrid}>
            <SummaryBlock label="Staircases required" value={safeCount(result?.number_of_staircases)} />
            <SummaryBlock label="Core width" value={formatNumber(result?.stair_core_width_m, 2, " m")} />
            <SummaryBlock label="Core length" value={formatNumber(result?.required_shaft_length_m, 2, " m")} />
            <SummaryBlock
              label="Controlling floor"
              value={
                typeof result?.controlling_floor_index === "number"
                  ? storeyName(result.controlling_floor_index, entranceIdx)
                  : "—"
              }
            />
          </View>
        </View>
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Floor-by-floor breakdown</Text>
          <FloorTableHeader />
          {floors.length === 0 ? (
            <Text style={[styles.muted, { marginTop: 8 }]}>No results available.</Text>
          ) : (
            floors.map((row) => <FloorTableRow key={row.floor_index} row={row} entranceIdx={entranceIdx} />)
          )}
        </View>
        <View style={styles.footer} fixed>
          <Text>Generated by Pebbledash</Text>
          <Text render={({ pageNumber, totalPages }) => `Page ${pageNumber} / ${totalPages}`} />
        </View>
      </Page>

      <Page size="A3" orientation="landscape" style={styles.page}>
        <MetadataHeader project={project} />
        <KeyFloorSummary floor={controllingFloor} entranceIdx={entranceIdx} />
        <PlaceholderDiagram />
        <View style={styles.footer} fixed>
          <Text>Generated by Pebbledash</Text>
          <Text render={({ pageNumber, totalPages }) => `Page ${pageNumber} / ${totalPages}`} />
        </View>
      </Page>
    </Document>
  );
};

export default StairPDFDocument;
